/* eslint-disable indent */
/* eslint-disable unicorn/prefer-module */
/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FaTrash } from "react-icons/fa";
import { RiAddLargeLine } from "react-icons/ri";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import cx from "classix";
import { useAuthContext } from "../../context/auth";
import { log, saveOportunidadBTL, getClientes, getEdificiosOperativos } from "../../utils";
import styles from "./stylesBTL.module.scss";

export const VentasBTL = () => {
  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
  const [temporalDataForm, setTemporalDataForm] = useState();
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [totales, setTotales] = useState({
    totalInvestment: 0,
    totalCommission: 0,
    netSale: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { pais, id } = useAuthContext();

  const logo = useMemo(
    () =>
      ({
        Chile: require("../../img/wecast-logo.png"),
        Perú: require("../../img/visiona-logo.png"),
        Uruguay: require("../../img/screenmedia-logo.png"),
        Region: require("../../img/adigital-logo.png"),
      })[pais] || require("../../img/visiona-logo.png"),
    [pais]
  );

  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
      })[pais] || "es-UY",
    []
  );

  const decimales = useCallback(
    (pais) =>
      ({
        Chile: 0,
        Perú: 2,
        Uruguay: 0,
      })[pais] || 0,
    []
  );

  const operacionTextual = useCallback(
    (pais) =>
      ({
        Chile: "WECAST",
        Perú: "VISIONA",
        Uruguay: "SCREENMEDIA",
      })[pais] || "VISIONA",
    []
  );

  const methods = useForm({
    defaultValues: {
      cliente: "",
      estado_oportunidad: "",
      mes_cierre: "",
      mes_factura: "",
      edificios: [{ edificio: "", monto: "", netoEdificio: "" }],
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
    control,
    trigger,
  } = methods;

  const formValues = watch();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "edificios",
  });

  // Fetch clients
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const data = await getClientes({ pais });
        setClientes(data);
      } catch (error) {
        toast.error("Error al cargar clientes");
        log.error("fetchClientes", error);
      }
    };
    fetchClientes();
  }, [pais]);

  // Fetch operational buildings
  useEffect(() => {
    const fetchEdificios = async () => {
      try {
        const data = await getEdificiosOperativos({ pais });
        setEdificios(data);
      } catch (error) {
        toast.error("Error al cargar edificios");
        log.error("fetchEdificios", error);
      }
    };
    fetchEdificios();
  }, [pais]);

  // Filtra y ordena los edificios alfabéticamente
  const edificiosFiltrados = edificios
    .filter((edificio) => edificio.pais === pais)
    .sort((a, b) => a.edificio.localeCompare(b.edificio));

  // Observa los valores de edificios para calcular los totales
  const edificiosValues = watch("edificios");

  // Recalcula los totales cada vez que cambian los valores de edificios
  useEffect(() => {
    const edificios = edificiosValues || [];
    const totalInvestment = edificios.reduce((sum, edificio) => {
      const monto = isNaN(parseInt(edificio?.monto, 10)) ? 0 : parseInt(edificio?.monto, 10);
      return sum + monto;
    }, 0);

    const totalNetoEdificio = edificios.reduce((sum, edificio) => {
      const netoEdificio = isNaN(parseInt(edificio?.netoEdificio, 10)) ? 0 : parseInt(edificio?.netoEdificio, 10);
      return sum + netoEdificio;
    }, 0);

    const netSale = totalInvestment - totalNetoEdificio;
    const totalCommission = totalInvestment ? (totalNetoEdificio / totalInvestment) * 100 : 0;

    setTotales({
      totalInvestment,
      totalCommission,
      netSale,
    });
  }, [edificiosValues]);

  // Obtener los IDs de edificios ya seleccionados (excluyendo el índice actual)
  const getEdificiosDisponibles = (currentIndex) => {
    const selectedEdificios = edificiosValues
      .map((edificio, index) => (index !== currentIndex ? edificio?.edificio : null))
      .filter(Boolean);
    return edificiosFiltrados.filter((edificio) => !selectedEdificios.includes(String(edificio.id)));
  };

  const canAddNewEdificio = async () => {
    const values = watch("edificios") || [];
    if (values.length === 0) return false;

    return values.every(
      (edificio) =>
        edificio?.edificio &&
        edificio?.edificio !== "" &&
        edificio?.monto &&
        edificio?.monto !== "" &&
        edificio?.netoEdificio &&
        edificio?.netoEdificio !== ""
    );
  };

  const handleAddEdificio = async () => {
    const canAdd = await canAddNewEdificio();
    if (canAdd) {
      append({ edificio: "", monto: "", netoEdificio: "" });
    } else {
      toast.error("Por favor, completa todos los campos requeridos antes de agregar un nuevo edificio.");
      await trigger("edificios");
    }
  };

  const onSubmit = useCallback(
    async (data) => {
      const isValid = await trigger();
      if (!isValid) return;

      setShowModalConfirmacion(true);

      const statusMap = {
        "50%": 50,
        "75%": 75,
        "90%": 90,
        "100%": 100,
      };

      const buildings = data.edificios
        .map((edificio) => {
          const parsedMonto = parseInt(edificio.monto, 10);
          const parsedNeto = parseInt(edificio.netoEdificio, 10);

          if (isNaN(parsedMonto) || parsedMonto < 1) {
            console.warn("Invalid monto:", edificio.monto);
            return null; // Skip invalid edificio
          }
          if (isNaN(parsedNeto) || parsedNeto < 0) {
            console.warn("Invalid netoEdificio:", edificio.netoEdificio);
            return null; // Skip invalid edificio
          }
          return {
            id: Number(edificio.edificio),
            importe: parsedMonto,
            comision: parsedNeto && parsedMonto ? (parsedNeto / parsedMonto) * 100 : 0,
          };
        })
        .filter(Boolean); // Remove null entries

      const totalInvestment = buildings.reduce((sum, building) => sum + building.importe, 0);
      const totalNetoEdificio = buildings.reduce((sum, building) => {
        const neto = (building.comision / 100) * building.importe;
        return sum + neto;
      }, 0);
      const netSale = totalInvestment - totalNetoEdificio;
      const totalCommission = totalInvestment ? (totalNetoEdificio / totalInvestment) * 100 : 0;

      const newOportunidad = {
        client_id: Number(data.cliente),
        total_investment: totalInvestment,
        total_commission: totalCommission,
        net_sale: netSale,
        buildings,
        created_by: id,
        executive_responsible: id,
        status: statusMap[data.estado_oportunidad],
        mes_cierre: parseInt(data.mes_cierre, 10),
        mes_factura: parseInt(data.mes_factura, 10),
        pais: pais,
      };

      setTemporalDataForm(newOportunidad);
    },
    [id, totales, trigger]
  );

  const saveButtonHandle = useCallback(async () => {
    try {
      setIsLoading(true);
      setShowModalConfirmacion(false);
      const result = await saveOportunidadBTL(temporalDataForm);
      console.log("Save result:", result);
      reset();
      toast.success("La oportunidad se guardó correctamente");
      setTimeout(() => {
        setShouldRefresh(true);
      }, 2000);
    } catch (error) {
      console.error("saveButtonHandle error:", error);
      toast.error("Hubo un error al guardar la oportunidad, prueba de nuevo.");
      log.error("saveButtonHandle", error);
    } finally {
      setIsLoading(false);
    }
  }, [temporalDataForm, reset]);

  useEffect(() => {
    if (shouldRefresh) {
      // window.location.reload();
    }
  }, [shouldRefresh]);

  const disableSubmit = useMemo(() => {
    const isGeneralInfoComplete =
      formValues.cliente && formValues.estado_oportunidad && formValues.mes_cierre && formValues.mes_factura;

    const areEdificiosComplete =
      Array.isArray(edificiosValues) &&
      edificiosValues.length > 0 &&
      edificiosValues.every((edificio) => {
        const monto = edificio?.monto !== "" && edificio?.monto !== undefined ? Number(edificio.monto) : null;
        const netoEdificio =
          edificio?.netoEdificio !== "" && edificio?.netoEdificio !== undefined ? Number(edificio.netoEdificio) : null;

        return (
          !!edificio?.edificio &&
          edificio.edificio !== "" &&
          monto !== null &&
          monto >= 1 &&
          netoEdificio !== null &&
          netoEdificio >= 0 &&
          netoEdificio <= monto
        );
      });

    return !(isGeneralInfoComplete && areEdificiosComplete);
  }, [formValues, edificiosValues]);

  useEffect(() => {
    const checkAndClearErrors = async () => {
      const values = watch("edificios") || [];
      values.forEach((edificio, index) => {
        const isValid =
          edificio?.edificio &&
          edificio?.edificio !== "" &&
          edificio?.monto &&
          edificio?.monto !== "" &&
          edificio?.netoEdificio &&
          edificio?.netoEdificio !== "";
        if (isValid) {
          methods.clearErrors(`edificios.${index}`);
        }
      });
    };
    checkAndClearErrors();
  }, [edificiosValues, methods]);

  return (
    <div className={styles["container-ventas-btl"]}>
      <div className={styles["logo-titulo"]}>
        <img src={logo} alt={`${pais} Logo`} className={styles["logo"]} />
        <h4>CARGAR OPORTUNIDAD BTL</h4>
      </div>
      <FormProvider {...methods}>
        <form className={styles["form"]} onSubmit={handleSubmit(onSubmit)}>
          {/* General Information Section */}
          <div className={styles["general-info-section"]}>
            <div>
              <select
                className={cx("form-select", errors?.cliente && "is-invalid")}
                id="cliente"
                {...register("cliente", { required: true })}
              >
                <option value="" hidden>
                  Cliente
                </option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </option>
                ))}
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>
            <div>
              <select
                className={cx("form-select", errors?.estado_oportunidad && "is-invalid")}
                id="estado_oportunidad"
                {...register("estado_oportunidad", { required: true })}
              >
                <option value="" hidden>
                  Status
                </option>
                <option value="100%">100%</option>
                <option value="90%">90%</option>
                <option value="75%">75%</option>
                <option value="50%">50%</option>
                <option value="0%">0%</option>
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>
            <div>
              <select
                className={cx("form-select", errors?.mes_cierre && "is-invalid")}
                id="mes_cierre"
                {...register("mes_cierre", { required: true })}
              >
                <option value="" hidden>
                  Mes cierre
                </option>
                <option value="01">ENE</option>
                <option value="02">FEB</option>
                <option value="03">MAR</option>
                <option value="04">ABR</option>
                <option value="05">MAY</option>
                <option value="06">JUN</option>
                <option value="07">JUL</option>
                <option value="08">AGO</option>
                <option value="09">SEP</option>
                <option value="10">OCT</option>
                <option value="11">NOV</option>
                <option value="12">DIC</option>
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>
            <div>
              <select
                className={cx("form-select", errors?.mes_factura && "is-invalid")}
                id="mes_factura"
                {...register("mes_factura", { required: true })}
              >
                <option value="" hidden>
                  Mes factura
                </option>
                <option value="01">ENE</option>
                <option value="02">FEB</option>
                <option value="03">MAR</option>
                <option value="04">ABR</option>
                <option value="05">MAY</option>
                <option value="06">JUN</option>
                <option value="07">JUL</option>
                <option value="08">AGO</option>
                <option value="09">SEP</option>
                <option value="10">OCT</option>
                <option value="11">NOV</option>
                <option value="12">DIC</option>
              </select>
              <div className="invalid-feedback">Este campo es obligatorio</div>
            </div>
          </div>

          {/* Buildings and Totals as a Table */}
          <div className={styles["table"]}>
            <table>
              <thead>
                <tr>
                  <th>EDIFICIO</th>
                  <th>INVERSIÓN</th>
                  <th>NETO EDIFICIO</th>
                  <th>Neto {operacionTextual(pais)}</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  const monto = Number(watch(`edificios.${index}.monto`)) || 0;
                  const netoEdificio = Number(watch(`edificios.${index}.netoEdificio`)) || 0;
                  const inversionNeta = monto - netoEdificio;

                  return (
                    <tr key={field.id}>
                      <td>
                        <select
                          className={cx("form-select", errors?.edificios?.[index]?.edificio && "is-invalid")}
                          {...register(`edificios.${index}.edificio`, { required: "Seleccione un edificio" })}
                        >
                          <option value="" className="placeholder-option">
                            Seleccionar
                          </option>
                          {getEdificiosDisponibles(index).map((edificio) => (
                            <option key={edificio.id} value={edificio.id}>
                              {edificio.edificio.toUpperCase()}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">{errors?.edificios?.[index]?.edificio?.message}</div>
                      </td>
                      <td>
                        <div>
                          <input
                            className={cx("form-control text-end", errors?.edificios?.[index]?.monto && "is-invalid")}
                            placeholder="Inversión"
                            type="number"
                            min="1"
                            {...register(`edificios.${index}.monto`, {
                              required: "Este campo es obligatorio",
                              valueAsNumber: true,
                              validate: (value) => value >= 1 || "El valor debe ser al menos 1",
                            })}
                          />
                          <div className="invalid-feedback">{errors?.edificios?.[index]?.monto?.message}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <input
                            className={cx(
                              "form-control text-end",
                              errors?.edificios?.[index]?.netoEdificio && "is-invalid"
                            )}
                            placeholder="Neto Edificio"
                            type="number"
                            min="0"
                            {...register(`edificios.${index}.netoEdificio`, {
                              required: "Este campo es obligatorio",
                              valueAsNumber: true,
                              validate: (value, formValues) => {
                                const monto = Number(formValues.edificios[index].monto) || 0;
                                return value <= monto || "El neto edificio no puede ser mayor al monto";
                              },
                            })}
                          />
                          <div className="invalid-feedback">{errors?.edificios?.[index]?.netoEdificio?.message}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <input
                            className="form-control text-end"
                            value={
                              inversionNeta
                                ? inversionNeta.toLocaleString(localString(pais), {
                                    minimumFractionDigits: decimales(pais),
                                    maximumFractionDigits: decimales(pais),
                                  })
                                : ""
                            }
                            placeholder="Inversión Neta"
                            readOnly
                          />
                        </div>
                      </td>
                      <td>
                        <div>
                          <button
                            type="button"
                            className={cx("btn", index === 0 && "d-none")}
                            onClick={() => remove(index)}
                            disabled={fields.length <= 1}
                          >
                            <FaTrash />
                          </button>
                          {index === fields.length - 1 && (
                            <button type="button" className="btn" onClick={handleAddEdificio}>
                              <RiAddLargeLine />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-center mt-4 gap-3">
            <button className="btn btn-primary" type="submit" disabled={disableSubmit || isLoading}>
              {!isLoading ? (
                "CARGAR"
              ) : (
                <>
                  <Spinner size="sm">CARGANDO...</Spinner>
                  <span> CARGANDO</span>
                </>
              )}
            </button>
          </div>

          <Modal isOpen={showModalConfirmacion} toggle={() => setShowModalConfirmacion(false)}>
            <ModalHeader>¿Está seguro?</ModalHeader>
            <ModalBody>Se está por guardar una nueva oportunidad</ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowModalConfirmacion(false)}>
                Cancelar
              </Button>
              <Button color="primary" onClick={saveButtonHandle}>
                Guardar
              </Button>
            </ModalFooter>
          </Modal>
        </form>
      </FormProvider>
    </div>
  );
};
